import { useState } from 'react';
import { supabase } from './supabase.js';
import { MONTH_KEYS, monthsInSeasonWindow } from './multi-year-analytics.js';

const COLORS = {
  bark: '#3D2B1F', gold: '#C9A96E', goldL: '#E8D5B0', cream: '#FFFDF8',
  sand: '#F5EFE6', olive: '#5A7247', sea: '#2B6F8A', terra: '#C0562B', stone: '#8B7D6B',
};

export default function MultiYearBackfillModal({
  year,
  property,
  existingPastSeason,
  realBookingsByMonth, // { jul: { revenue, nights }, ... } for months with bookings in this year
  seasonWindow,
  t,
  lang,
  onClose,
  onSaved,
}) {
  const hasOverlap = Object.keys(realBookingsByMonth).length > 0;

  const [showMonthly, setShowMonthly] = useState(
    hasOverlap || !!(existingPastSeason && existingPastSeason.months)
  );
  const [showSource, setShowSource] = useState(
    !!(existingPastSeason && existingPastSeason.source_split)
  );

  const [totalRevenue, setTotalRevenue] = useState(
    existingPastSeason?.total_revenue != null ? String(existingPastSeason.total_revenue) : ''
  );
  const [totalNights, setTotalNights] = useState(
    existingPastSeason?.total_nights != null ? String(existingPastSeason.total_nights) : ''
  );

  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  // monthsByKey: { may: { revenue: '4500', nights: '38' }, ... } — strings for input controls
  const initialMonths = (() => {
    const out = {};
    const seasonMonths = monthsInSeasonWindow(seasonWindow.startMd, seasonWindow.endMd);
    for (const m of seasonMonths) out[m] = { revenue: '', nights: '' };
    if (existingPastSeason?.months) {
      const windowSet = new Set(seasonMonths);
      for (const [k, v] of Object.entries(existingPastSeason.months)) {
        if (!windowSet.has(k)) continue; // skip keys outside current season window
        out[k] = {
          revenue: v?.revenue != null ? String(v.revenue) : '',
          nights:  v?.nights  != null ? String(v.nights)  : '',
        };
      }
    }
    return out;
  })();
  const [monthsByKey, setMonthsByKey] = useState(initialMonths);
  const seasonMonthList = monthsInSeasonWindow(seasonWindow.startMd, seasonWindow.endMd);

  const [srcDirect, setSrcDirect] = useState(
    existingPastSeason?.source_split?.direct != null ? String(existingPastSeason.source_split.direct) : ''
  );
  const [srcBcom, setSrcBcom] = useState(
    existingPastSeason?.source_split?.booking_com != null ? String(existingPastSeason.source_split.booking_com) : ''
  );
  const [srcOther, setSrcOther] = useState(
    existingPastSeason?.source_split?.other != null ? String(existingPastSeason.source_split.other) : ''
  );

  // Auto-derive total revenue/nights from monthly when in monthly mode
  const monthlyTotalRevenue = seasonMonthList.reduce((sum, m) => {
    if (realBookingsByMonth[m]) return sum + (Number(realBookingsByMonth[m].revenue) || 0);
    return sum + (Number(monthsByKey[m]?.revenue) || 0);
  }, 0);
  const monthlyTotalNights = (() => {
    let sum = 0;
    let anyKnown = false;
    for (const m of seasonMonthList) {
      if (realBookingsByMonth[m]) {
        sum += Number(realBookingsByMonth[m].nights) || 0;
        anyKnown = true;
      } else {
        const n = monthsByKey[m]?.nights;
        if (n !== '' && n != null) {
          sum += Number(n) || 0;
          anyKnown = true;
        }
      }
    }
    return anyKnown ? sum : null;
  })();

  async function onSave() {
    setError(null);
    const totalRevNum = showMonthly ? monthlyTotalRevenue : Number(totalRevenue);
    if (!showMonthly && (!totalRevenue || Number.isNaN(totalRevNum) || totalRevNum < 0)) {
      setError(t.analytics.backfill.revenueRequiredError);
      return;
    }

    let monthsJson = null;
    if (showMonthly) {
      monthsJson = {};
      for (const m of seasonMonthList) {
        if (realBookingsByMonth[m]) continue; // never write real-bookings months to backfill
        const cell = monthsByKey[m] || { revenue: '', nights: '' };
        const rev = cell.revenue !== '' ? Number(cell.revenue) : null;
        const nts = cell.nights  !== '' ? Number(cell.nights)  : null;
        if (rev == null || rev <= 0) continue;
        monthsJson[m] = { revenue: rev };
        if (nts != null) monthsJson[m].nights = nts;
      }
      if (Object.keys(monthsJson).length === 0 && !hasOverlap) {
        // No months entered and no overlap → fall back to season-total mode by clearing monthly
        monthsJson = null;
      }
    }

    let sourceSplit = null;
    const anySrc = srcDirect !== '' || srcBcom !== '' || srcOther !== '';
    if (showSource && anySrc) {
      const d = Number(srcDirect) || 0;
      const b = Number(srcBcom)   || 0;
      const o = Number(srcOther)  || 0;
      const sum = d + b + o;
      // Allow 1-cent rounding tolerance
      if (Math.abs(sum - totalRevNum) > 0.01) {
        setError(
          t.analytics.backfill.sourceSumError
            .replace('{sum}',   Math.round(sum * 100) / 100)
            .replace('{total}', Math.round(totalRevNum * 100) / 100)
        );
        return;
      }
      sourceSplit = { direct: d, booking_com: b, other: o };
    }

    setSaving(true);
    const row = {
      property_id: property.id,
      year,
      total_revenue: totalRevNum,
      total_nights: showMonthly ? monthlyTotalNights : (totalNights ? Number(totalNights) : null),
      months: monthsJson,
      source_split: sourceSplit,
    };
    const { error: dbErr } = await supabase
      .from('past_seasons')
      .upsert(row, { onConflict: 'property_id,year' });
    setSaving(false);
    if (dbErr) { setError(dbErr.message); return; }
    await onSaved?.();
  }

  async function onRemove() {
    if (!existingPastSeason) return;
    if (!window.confirm(t.analytics.backfill.removeConfirm.replace('{year}', year))) return;
    setSaving(true);
    const { error: dbErr } = await supabase
      .from('past_seasons')
      .delete()
      .eq('id', existingPastSeason.id);
    setSaving(false);
    if (dbErr) { setError(dbErr.message); return; }
    await onSaved?.();
  }

  const titleTpl = existingPastSeason ? t.analytics.backfill.editTitle : t.analytics.backfill.title;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
      <div style={{ background: COLORS.cream, borderRadius: 8, padding: 20, maxWidth: 520, width: '100%', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 8px 24px rgba(0,0,0,0.2)' }}>
        <h2 style={{ fontFamily: "'Cormorant Garamond', serif", margin: '0 0 16px', color: COLORS.bark, fontSize: 22, fontWeight: 500, lineHeight: 1.1 }}>
          {titleTpl.replace('{year}', year)}
        </h2>

        {/* Total revenue */}
        <label style={{ display: 'block', marginBottom: 12 }}>
          <div style={{ fontSize: 13, color: COLORS.bark, marginBottom: 4 }}>
            {t.analytics.backfill.revenueLabel}
            <span style={{ color: COLORS.terra, marginLeft: 4 }}>*</span>
          </div>
          <input
            type="number"
            min="0"
            step="0.01"
            value={showMonthly ? String(Math.round(monthlyTotalRevenue * 100) / 100) : totalRevenue}
            onChange={e => setTotalRevenue(e.target.value)}
            disabled={showMonthly}
            style={inputStyle}
          />
        </label>

        {/* Total nights (optional) */}
        <label style={{ display: 'block', marginBottom: 12 }}>
          <div style={{ fontSize: 13, color: COLORS.bark, marginBottom: 4 }}>
            {t.analytics.backfill.nightsLabel}
          </div>
          <input
            type="number"
            min="0"
            step="1"
            value={showMonthly ? (monthlyTotalNights ?? '') : totalNights}
            onChange={e => setTotalNights(e.target.value)}
            disabled={showMonthly}
            style={inputStyle}
          />
          <div style={{ fontSize: 11, color: COLORS.stone, marginTop: 2 }}>{t.analytics.backfill.nightsHelp}</div>
        </label>

        {/* Disclosure: Monthly (placeholder for Task 10) */}
        {!hasOverlap && (
          <button
            type="button"
            onClick={() => setShowMonthly(v => !v)}
            style={disclosureBtn}
          >
            {showMonthly ? '▾' : '▸'} {t.analytics.backfill.toggleMonthly}
          </button>
        )}

        {showMonthly && (
          <div style={{ background: COLORS.sand, padding: 12, borderRadius: 4, marginTop: 4, marginBottom: 12 }}>
            {hasOverlap && (
              <div style={{ background: '#FFF7E6', border: `1px solid ${COLORS.gold}`, padding: 8, borderRadius: 4, fontSize: 12, color: COLORS.bark, marginBottom: 8 }}>
                {t.analytics.backfill.overlapBanner
                  .replace('{months}', Object.keys(realBookingsByMonth).map(m => m.charAt(0).toUpperCase() + m.slice(1)).join(', '))
                  .replace('{year}',   year)}
              </div>
            )}
            <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.bark, marginBottom: 6 }}>
              {t.analytics.backfill.monthlyHeader}
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left',  padding: 4, color: COLORS.stone, fontWeight: 500 }}></th>
                  <th style={{ textAlign: 'right', padding: 4, color: COLORS.stone, fontWeight: 500 }}>{t.analytics.backfill.monthRevenue}</th>
                  <th style={{ textAlign: 'right', padding: 4, color: COLORS.stone, fontWeight: 500 }}>{t.analytics.backfill.monthNights}</th>
                </tr>
              </thead>
              <tbody>
                {seasonMonthList.map(m => {
                  const locked = !!realBookingsByMonth[m];
                  const realCell = realBookingsByMonth[m];
                  const cell = monthsByKey[m] || { revenue: '', nights: '' };
                  return (
                    <tr key={m}>
                      <td style={{ padding: 4, color: COLORS.bark, textTransform: 'capitalize' }}>{m}</td>
                      <td style={{ padding: 4 }}>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={locked ? Math.round(realCell.revenue * 100) / 100 : cell.revenue}
                          disabled={locked}
                          onChange={e => setMonthsByKey(prev => ({ ...prev, [m]: { ...prev[m], revenue: e.target.value } }))}
                          style={{ ...inputStyle, padding: '4px 6px', textAlign: 'right' }}
                        />
                        {locked && <div style={{ fontSize: 10, color: COLORS.stone, textAlign: 'right' }}>{t.analytics.backfill.overlapMonthLocked}</div>}
                      </td>
                      <td style={{ padding: 4 }}>
                        <input
                          type="number"
                          min="0"
                          step="1"
                          value={locked ? realCell.nights : cell.nights}
                          disabled={locked}
                          onChange={e => setMonthsByKey(prev => ({ ...prev, [m]: { ...prev[m], nights: e.target.value } }))}
                          style={{ ...inputStyle, padding: '4px 6px', textAlign: 'right' }}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Disclosure: Source split */}
        <button
          type="button"
          onClick={() => setShowSource(v => !v)}
          style={disclosureBtn}
        >
          {showSource ? '▾' : '▸'} {t.analytics.backfill.toggleSource}
        </button>

        {showSource && (
          <div style={{ background: COLORS.sand, padding: 12, borderRadius: 4, marginTop: 4, marginBottom: 12 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
              <label>
                <div style={{ fontSize: 12, color: COLORS.bark, marginBottom: 4 }}>{t.analytics.backfill.sourceDirect}</div>
                <input type="number" min="0" step="0.01" value={srcDirect}
                       onChange={e => setSrcDirect(e.target.value)} style={inputStyle} />
              </label>
              <label>
                <div style={{ fontSize: 12, color: COLORS.bark, marginBottom: 4 }}>{t.analytics.backfill.sourceBcom}</div>
                <input type="number" min="0" step="0.01" value={srcBcom}
                       onChange={e => setSrcBcom(e.target.value)} style={inputStyle} />
              </label>
              <label>
                <div style={{ fontSize: 12, color: COLORS.bark, marginBottom: 4 }}>{t.analytics.backfill.sourceOther}</div>
                <input type="number" min="0" step="0.01" value={srcOther}
                       onChange={e => setSrcOther(e.target.value)} style={inputStyle} />
              </label>
            </div>
          </div>
        )}

        {error && (
          <div style={{ background: '#FDECEA', color: COLORS.terra, padding: 8, borderRadius: 4, fontSize: 12, marginTop: 12 }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 20 }}>
          {existingPastSeason ? (
            <button onClick={onRemove} disabled={saving} style={{ ...secondaryBtn, color: COLORS.terra, borderColor: COLORS.terra }}>
              {t.analytics.backfill.remove}
            </button>
          ) : <span />}
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={onClose} disabled={saving} style={secondaryBtn}>{t.analytics.backfill.cancel}</button>
            <button onClick={onSave} disabled={saving} style={primaryBtn}>{t.analytics.backfill.save}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

const inputStyle = {
  width: '100%', padding: '8px 10px', border: `1px solid ${COLORS.stone}`,
  borderRadius: 4, fontSize: 14, color: COLORS.bark, background: 'white', boxSizing: 'border-box',
};

const disclosureBtn = {
  display: 'block', background: 'none', border: 'none', color: COLORS.bark,
  fontSize: 13, cursor: 'pointer', padding: '6px 0', textAlign: 'left',
};

const primaryBtn = {
  background: COLORS.bark, color: COLORS.cream, border: 'none',
  padding: '8px 16px', borderRadius: 4, cursor: 'pointer', fontSize: 13, fontWeight: 600,
};

const secondaryBtn = {
  background: 'transparent', color: COLORS.bark, border: `1px solid ${COLORS.bark}`,
  padding: '8px 16px', borderRadius: 4, cursor: 'pointer', fontSize: 13,
};
